import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import InlineCode from "@/components/InlineCode";
import { txToast } from "@/components/TxToaster";
import { useBackendApi } from "@/hooks/fetch";
import type { AdminActionReq, AdminActionResp, AdminListItem } from "@shared/adminApiTypes";
import { useState } from "react";
import PermissionPicker from "./PermissionPicker";

type Props = {
    /** undefined when creating a new admin */
    admin?: AdminListItem;
    registeredPermissions: Record<string, string>;
    onClose: () => void;
    onSaved: () => void;
};

/** Mirrors consts.regexValidFivemUsername on the backend, so we fail before the request. */
const NAME_RE = /^\w[\w.-]{1,18}\w$/;

/** The providers come back as objects; the dialog edits the raw id strings. */
const providerId = (admin: AdminListItem | undefined, key: string) => {
    return admin?.providers?.[key]?.id ?? '';
};

export default function AdminEditDialog({ admin, registeredPermissions, onClose, onSaved }: Props) {
    const isEditing = !!admin;
    const [name, setName] = useState(admin?.name ?? '');
    const [citizenfxID, setCitizenfxID] = useState(providerId(admin, 'citizenfx'));
    const [discordID, setDiscordID] = useState(providerId(admin, 'discord'));
    const [permissions, setPermissions] = useState<string[]>(admin?.permissions ?? []);
    const [isSaving, setIsSaving] = useState(false);
    //Set once an admin was created - the password is shown exactly once
    const [newPassword, setNewPassword] = useState<string | null>(null);

    const actionApi = useBackendApi<AdminActionResp, AdminActionReq>({
        method: 'POST',
        path: isEditing ? '/adminManager/edit' : '/adminManager/add',
    });

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (isSaving) return;

        const trimmedName = name.trim();
        if (!isEditing && !NAME_RE.test(trimmedName)) {
            return txToast.warning('Der Benutzername darf nur Buchstaben, Zahlen und _ . - enthalten (3-20 Zeichen).');
        }
        if (!citizenfxID.trim() && !discordID.trim()) {
            return txToast.warning('Mindestens eine ID wird gebraucht, sonst kann sich der Admin nicht anmelden.');
        }

        setIsSaving(true);
        actionApi({
            data: {
                name: trimmedName,
                citizenfxID: citizenfxID.trim(),
                discordID: discordID.trim(),
                permissions,
            },
            toastLoadingMessage: isEditing ? 'Admin wird gespeichert...' : 'Admin wird angelegt...',
            success: (data, toastId) => {
                if (data.type === 'danger') {
                    //markdown responses carry ** and <br>, which would read badly in a toast
                    txToast.error(data.message.replace(/<br\s*\/?>/g, ' ').replace(/\*\*/g, ''), { id: toastId });
                    return;
                }
                if (data.type === 'showPassword') {
                    txToast.success('Admin angelegt.', { id: toastId });
                    setNewPassword(data.password);
                    return;
                }
                txToast.success('Admin gespeichert.', { id: toastId });
                onSaved();
            },
            finally: () => setIsSaving(false),
        });
    };

    //After creating, the dialog turns into the one-time password notice
    if (newPassword) {
        return (
            <Dialog open onOpenChange={() => onSaved()}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Admin {name} angelegt</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                            Das Passwort wird <strong>nur jetzt</strong> angezeigt. Gib es weiter,
                            der Admin ändert es nach dem ersten Login selbst.
                        </p>
                        <div className="plate p-3 text-center">
                            <InlineCode className="text-value tracking-widest">{newPassword}</InlineCode>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="solid" onClick={() => onSaved()}>Notiert</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open onOpenChange={(open) => !open && !isSaving && onClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? `Admin bearbeiten: ${admin!.name}` : 'Neuer Admin'}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="contents">
                    <div className="space-y-5 overflow-y-auto pr-2 -mr-2">
                        <div className="grid gap-4 sm:grid-cols-3">
                            <div className="space-y-1">
                                <Label htmlFor="admin-name">Benutzername</Label>
                                <Input
                                    id="admin-name"
                                    value={name}
                                    maxLength={20}
                                    placeholder="z.B. Harald"
                                    //the name is the identity of the record, so it cannot change
                                    disabled={isEditing || isSaving}
                                    autoFocus={!isEditing}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="admin-cfx">Cfx.re / FiveM</Label>
                                <Input
                                    id="admin-cfx"
                                    value={citizenfxID}
                                    placeholder="Forum-Name oder fivem:1234567"
                                    disabled={isSaving}
                                    onChange={(e) => setCitizenfxID(e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="admin-discord">Discord-ID</Label>
                                <Input
                                    id="admin-discord"
                                    value={discordID}
                                    placeholder="440549204725596170"
                                    disabled={isSaving}
                                    onChange={(e) => setDiscordID(e.target.value)}
                                />
                            </div>
                        </div>
                        <p className="text-caption text-faint">
                            Eine der beiden IDs genügt – darüber meldet sich der Admin an.
                            Der Cfx.re-Name wird beim Speichern gegen das Forum geprüft.
                        </p>

                        <div className="space-y-2">
                            <h3 className="font-semibold">Berechtigungen</h3>
                            <PermissionPicker
                                registered={registeredPermissions}
                                value={permissions}
                                onChange={setPermissions}
                                disabled={isSaving}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="muted" onClick={onClose} disabled={isSaving}>
                            Abbrechen
                        </Button>
                        <Button type="submit" variant="solid" disabled={isSaving}>
                            {isEditing ? 'Speichern' : 'Anlegen'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
